import {
	ColorMatchImageRecord,
	fetchColorMatchImages,
	storeColorMatchImage,
} from "@/storage/indexeddb"
import { isAxiosError } from "axios"
import { PNApiOperations } from "@/api/operations"
import { queryClient } from "@/api/constants"
import { CreateImageRequestDTO, ImageDTO, ImageUsageType } from "@/api/dto/images"
import { GetUserResponseBody } from "@/api/dto/users"
import { SignedUrlDTO } from "@/api/dto/upload"
import { fastHashFileContent } from "@/utils/hash"
import { Sentry } from "@/telemetry/sentry"
import { fetchWithNetworkBehavior } from "@/api/fetch-with-network-behavior"
import { setColorMatchImageUploadStatus } from "./color-match-upload-state"

export interface ColorMatchImageItem {
	id: string
	createdAt: Date
	updatedAt: Date
	name: string
	contentUrl?: string
	data?: Blob
	contentDigest?: string
	userId?: string
}

export interface SampleResponseDTO {
	collections: SampleCollectionDTO[]
}

export interface SampleCollectionDTO {
	name: string
	images: ImageResponseDTO[]
}

export interface ImageResponseDTO {
	contentHash: string
	displayName: string
	distName: string
	url: string
}

export class ColorMatchImageService {
	static queryKey = "color-match-images"
	static syncImages = async (): Promise<void> => {
		const images = await this.listImages(true)
		queryClient.setQueryData(ColorMatchImageService.getCurrentQueryKey(), images)
	}

	static fetchImages = async () => {
		return this.listImages(false)
	}

	private static listImages = async (syncImages: boolean = false) => {
		const images: ColorMatchImageItem[] = []
		// Color match is capped at 50 source images (enforced on import), so fetch at most 50.
		const { data: backendImages } = await PNApiOperations.listImages({ size: 50 })

		const indexeddbImageRecords = await fetchColorMatchImages()
		const currentUserId = queryClient.getQueryData<GetUserResponseBody>(["user"])?.userId

		//Merge backend images with local images
		for (const dto of backendImages) {
			const localIndex = indexeddbImageRecords.findIndex(
				(record) => record.id === dto.imageId,
			)

			const localRecord = localIndex !== -1 ? indexeddbImageRecords.at(localIndex) : undefined

			if (localRecord && (!localRecord.userId || localRecord.userId === dto.userId)) {
				const image = ColorMatchImageService.mergeRecordAndDTO(localRecord, dto)
				images.push(image)
			} else {
				const image = ColorMatchImageService.dtoToImageItem(dto)
				images.push(image)
			}

			if (localRecord) {
				indexeddbImageRecords.splice(localIndex, 1)
			}
		}

		const currentUserLocalRecords = indexeddbImageRecords.filter((record) =>
			currentUserId ? record.userId === currentUserId : false,
		)

		//Remaining unsynced local images
		for (const record of currentUserLocalRecords) {
			images.push(ColorMatchImageService.recordToImageItem(record))
		}

		if (syncImages && currentUserLocalRecords.length > 0) {
			//Also upload and create image to BE for remaining unsynced local images
			//shouldn't really happen unless it somehow failed to upload during import
			void this.uploadColorMatchImages(currentUserLocalRecords)
		}

		images.sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
		return images
	}

	static createImage = async (body: CreateImageRequestDTO) => {
		const dto = await PNApiOperations.createImage(body)
		await queryClient.invalidateQueries({ queryKey: [ColorMatchImageService.queryKey] })
		return ColorMatchImageService.dtoToImageItem(dto)
	}

	static deleteImage = async (imageId: string) => {
		await PNApiOperations.deleteImage(imageId)
		await queryClient.invalidateQueries({ queryKey: [ColorMatchImageService.queryKey] })
	}

	static downloadImageContent = async (imageItem: ColorMatchImageItem) => {
		if ((imageItem.data && imageItem.data instanceof Blob) || !imageItem.contentUrl) {
			return
		}
		const data = await fetchWithNetworkBehavior(imageItem.contentUrl, undefined, {
			networkIntent: "active",
		}).then((response) => response.blob())
		if (!data) {
			return
		}
		const userId =
			imageItem.userId ?? queryClient.getQueryData<GetUserResponseBody>(["user"])?.userId
		if (!userId) {
			return data
		}
		// Also stores the image data in the ColorMatchImageRecord for future use
		const record: ColorMatchImageRecord = {
			id: imageItem.id,
			name: imageItem.name,
			data: data,
			createdAt: new Date(imageItem.createdAt),
			updatedAt: new Date(imageItem.updatedAt),
			userId,
		}
		await storeColorMatchImage(record)
		void queryClient.invalidateQueries({ queryKey: [ColorMatchImageService.queryKey] })
		return data
	}

	static recordToImageItem = (record: ColorMatchImageRecord): ColorMatchImageItem => {
		return {
			id: record.id,
			createdAt: new Date(record.createdAt),
			updatedAt: new Date(record.updatedAt),
			name: record.name,
			data: record.data,
			contentDigest: record.contentDigest,
			userId: record.userId,
		}
	}

	static dtoToImageItem = (dto: ImageDTO): ColorMatchImageItem => {
		return {
			id: dto.imageId,
			createdAt: new Date(dto.createdAt),
			updatedAt: new Date(dto.updatedAt),
			name: dto.name,
			contentUrl: dto.contentUrl,
			contentDigest: dto.contentDigest,
			userId: dto.userId,
		}
	}

	static mergeRecordAndDTO = (
		record: ColorMatchImageRecord,
		dto: ImageDTO,
	): ColorMatchImageItem => {
		if (record.id !== dto.imageId) {
			throw new Error("Mismatched record and DTO")
		}
		return {
			id: dto.imageId,
			createdAt: new Date(dto.createdAt),
			updatedAt: new Date(dto.updatedAt),
			name: dto.name,
			data: record.data,
			contentUrl: dto.contentUrl,
			contentDigest: dto.contentDigest,
			userId: dto.userId,
		}
	}

	private static getCurrentQueryKey = () => {
		const userId = queryClient.getQueryData<GetUserResponseBody>(["user"])?.userId
		return userId
			? [ColorMatchImageService.queryKey, userId]
			: [ColorMatchImageService.queryKey]
	}

	/**
	 *
	 * Uploads the image contents of the supplied color match image records to s3.
	 *
	 * Side effects:
	 *   - creates ColorImage on BE
	 *
	 * @param records Array of color match image records containing images to upload
	 * @returns array of PromiseSettledResult for the individual upload operations
	 *
	 */
	static async uploadColorMatchImages(records: ColorMatchImageRecord[]) {
		records.forEach((record) => setColorMatchImageUploadStatus(record.id, "uploading"))

		let uploadUrls: SignedUrlDTO[]
		try {
			uploadUrls = await PNApiOperations.getSignedUrls(
				records.map(() => ({
					contentType: "image",
					entityType: "color-match-template",
					contentDigest: undefined,
				})),
			)
		} catch (error) {
			records.forEach((record) => setColorMatchImageUploadStatus(record.id, "failed"))
			throw error
		}

		await Promise.allSettled(
			records.map(async (record, index) => {
				try {
					const uploadUrl = uploadUrls[index]

					if (!uploadUrl) {
						throw new Error("Couldn't match thumbnail with upload url")
					}

					const { url } = await PNApiOperations.uploadContent({
						data: record.data,
						url: uploadUrl.url,
					})

					await this.createColorMatchImage(record, url)
					setColorMatchImageUploadStatus(record.id, "synced")

					return record.id
				} catch (error) {
					setColorMatchImageUploadStatus(record.id, "failed")
					throw error
				}
			}),
		).then((results) => {
			let errorCount = 0
			results.map((r) => {
				if (r.status === "rejected") {
					const error = r.reason as Error
					errorCount++
					Sentry.captureException(error)
					console.error("Failed to upload color match image", error)
				}
			})

			if (errorCount > 0) {
				console.error(
					errorCount > 1
						? `Failed to upload ${errorCount} color match images.`
						: "Failed to upload 1 color match image.",
				)
			}

			void queryClient.invalidateQueries({ queryKey: [ColorMatchImageService.queryKey] })
		})
	}

	static async createColorMatchImage(record: ColorMatchImageRecord, url: string) {
		const contentDigest = record.contentDigest ?? (await fastHashFileContent(record.data))

		try {
			await ColorMatchImageService.createImage({
				imageId: record.id,
				createdAt: record.createdAt,
				name: record.name,
				contentUrl: url,
				contentType: "image/jpeg",
				contentDigest,
				usageType: ImageUsageType.colorMatchTemplate,
			})
		} catch (error) {
			console.error("[ColorMatchImages] create failed", {
				imageId: record.id,
				name: record.name,
				contentDigest,
				usedStoredDigest: Boolean(record.contentDigest),
				error: isAxiosError<unknown>(error)
					? {
							message: error.message,
							code: error.code,
							status: error.response?.status,
							method: error.config?.method,
							url: error.config?.url,
						}
					: error,
			})
			throw error
		}

		return record.id
	}
}
